/**
 * Single source of truth for "which deployment is this?" across every
 * telemetry sink (PostHog events, PostHog exceptions, OTLP logs).
 *
 * Every value is read through a static `process.env.X` reference so the
 * Next.js compiler can inline the NEXT_PUBLIC_ ones into the client bundle —
 * dynamic `process.env[key]` lookups return undefined in the browser.
 */

export type DeploymentEnvironment = "production" | "preview" | "development";

export function classifyDeploymentEnvironment(
  value: string | undefined,
): DeploymentEnvironment {
  if (value === "production") return "production";
  if (value === "preview") return "preview";
  return "development";
}

export function resolveDeploymentEnvironment(): DeploymentEnvironment {
  // A deployed env file is sometimes used by `next dev`. Its explicit label
  // must not turn development compiler/SSR errors into production incidents.
  if (process.env.NODE_ENV === "development") return "development";
  // `next build` and `next start` force NODE_ENV=production locally. Treating
  // NODE_ENV as a deployment signal therefore sends a developer's events,
  // errors, replays, logs, traces and metrics into the real project. Only an
  // explicit deployment environment is allowed to turn delivery on.
  return classifyDeploymentEnvironment(process.env.NEXT_PUBLIC_DEPLOYMENT_ENV);
}

export const deploymentEnvironment = resolveDeploymentEnvironment();

/**
 * Master network-delivery gate for every PostHog signal. Preview remains
 * observable (and is filterable by deployment_environment); local dev,
 * local standalone, tests, scripts and unlabelled CI are always silent.
 */
export const telemetryDeliveryEnabled =
  deploymentEnvironment === "production" || deploymentEnvironment === "preview";

export function isLocalTelemetryHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase().replace(/^\[|\]$/g, "");
  if (
    normalized === "localhost" ||
    normalized.endsWith(".localhost") ||
    normalized === "::1" ||
    normalized === "0.0.0.0" ||
    normalized.startsWith("127.") ||
    normalized.startsWith("10.") ||
    normalized.startsWith("192.168.")
  ) {
    return true;
  }

  const private172 = normalized.match(/^172\.(\d{1,2})\./)?.[1];
  return private172
    ? Number(private172) >= 16 && Number(private172) <= 31
    : false;
}

/** Defense in depth if a production-looking env file is used on a dev URL. */
export function isClientTelemetryDeliveryEnabled(): boolean {
  if (!telemetryDeliveryEnabled) return false;
  if (typeof window === "undefined") return true;
  return !isLocalTelemetryHostname(window.location.hostname);
}

/**
 * True only for the real production deployment. Preview deployments must not
 * be treated as production for telemetry routing even though they are
 * production *builds*.
 */
export const isProductionDeployment = deploymentEnvironment === "production";

/** Short SHA is what humans paste into a dashboard filter. */
export const releaseCommitSha = process.env.NEXT_PUBLIC_GIT_COMMIT_SHA;
export const releaseShortSha = releaseCommitSha?.slice(0, 7);
export const releaseBranch = process.env.NEXT_PUBLIC_GIT_COMMIT_REF;

/**
 * The release identifier used by PostHog Error Tracking to group regressions
 * by deploy. Falls back to the app version when there is no git context, so
 * native builds (which ship a version, not a SHA) still group.
 */
export function buildReleaseId(appVersion: string): string {
  return releaseShortSha ? `${appVersion}+${releaseShortSha}` : appVersion;
}

/**
 * Properties stamped onto every event, exception and log record. Keep the
 * key names stable — dashboards filter on them.
 */
export function buildTelemetryContext(appVersion: string) {
  return {
    deployment_environment: deploymentEnvironment,
    release: buildReleaseId(appVersion),
    app_version: appVersion,
    ...(releaseShortSha ? { commit_sha: releaseShortSha } : {}),
    ...(releaseBranch ? { git_branch: releaseBranch } : {}),
  };
}
